import { useEffect, useRef, useState } from 'react';

/**
 * LaunchAtFields — the launch date & time as plain numeric boxes (month / day / year, hour : minute,
 * AM/PM) instead of a native datetime-local input. Android browsers render datetime-local
 * inconsistently (in desktop-site mode a tap does nothing and no keypad ever opens); numeric text
 * boxes always bring up the keypad.
 *
 * `value` / `onChange` use the same "YYYY-MM-DDTHH:MM" (24h, Eastern wall-clock) string the
 * datetime-local input produced. All boxes empty → ''. Anything incomplete or impossible →
 * LAUNCH_AT_INVALID, so the parent can refuse to save instead of silently clearing the date.
 */
export const LAUNCH_AT_INVALID = 'invalid';

const EMPTY = { month: '', day: '', year: '', hour: '', minute: '', ampm: 'PM' };

const partsFromValue = (value) => {
  const m = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!m) return EMPTY;
  const h24 = Number(m[4]) % 24;
  return {
    month: String(Number(m[2])),
    day: String(Number(m[3])),
    year: m[1],
    hour: String(h24 % 12 === 0 ? 12 : h24 % 12),
    minute: m[5],
    ampm: h24 >= 12 ? 'PM' : 'AM',
  };
};

const pad2 = (n) => String(n).padStart(2, '0');

const valueFromParts = (p) => {
  if (!p.month && !p.day && !p.year && !p.hour && !p.minute) return '';
  const month = Number(p.month);
  const day = Number(p.day);
  const year = Number(p.year);
  const hour = Number(p.hour);
  const minute = Number(p.minute);
  if (p.year.length !== 4 || !p.minute) return LAUNCH_AT_INVALID;
  if (!(month >= 1 && month <= 12 && hour >= 1 && hour <= 12 && minute >= 0 && minute <= 59)) return LAUNCH_AT_INVALID;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  if (!(day >= 1 && day <= daysInMonth)) return LAUNCH_AT_INVALID;
  const h24 = (hour % 12) + (p.ampm === 'PM' ? 12 : 0);
  return `${year}-${pad2(month)}-${pad2(day)}T${pad2(h24)}:${pad2(minute)}`;
};

const boxStyle = {
  background: 'rgba(0,0,0,0.34)',
  border: '1px solid rgba(255,255,255,0.12)',
  color: 'var(--sc-text)',
  fontVariantNumeric: 'tabular-nums',
};

const NumberBox = ({ id, label, value, maxLength, placeholder, width, onChange }) => (
  <input
    id={id}
    type="text"
    inputMode="numeric"
    pattern="[0-9]*"
    autoComplete="off"
    aria-label={label}
    value={value}
    maxLength={maxLength}
    placeholder={placeholder}
    onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, maxLength))}
    className="rounded-xl px-2 py-2.5 text-[16px] text-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FFC20E]/60"
    style={{ ...boxStyle, width }}
    data-testid={id}
  />
);

const LaunchAtFields = ({ id, value, onChange }) => {
  const [parts, setParts] = useState(() => partsFromValue(value));
  // The last value this component emitted: a different incoming value (load, save, reset) resyncs the boxes.
  const emitted = useRef(value);

  useEffect(() => {
    if (value !== emitted.current) {
      emitted.current = value;
      setParts(partsFromValue(value));
    }
  }, [value]);

  const update = (key, v) => {
    const next = { ...parts, [key]: v };
    setParts(next);
    const out = valueFromParts(next);
    emitted.current = out;
    onChange(out);
  };

  const sep = (
    <span className="text-[16px]" style={{ color: 'var(--sc-text-mute)' }} aria-hidden="true">
      /
    </span>
  );

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={id}>
      <div className="flex items-center gap-1.5">
        <NumberBox id={`${id}-month`} label="Month" value={parts.month} maxLength={2} placeholder="MM" width={52} onChange={(v) => update('month', v)} />
        {sep}
        <NumberBox id={`${id}-day`} label="Day" value={parts.day} maxLength={2} placeholder="DD" width={52} onChange={(v) => update('day', v)} />
        {sep}
        <NumberBox id={`${id}-year`} label="Year" value={parts.year} maxLength={4} placeholder="YYYY" width={72} onChange={(v) => update('year', v)} />
      </div>
      <div className="flex items-center gap-1.5">
        <NumberBox id={`${id}-hour`} label="Hour" value={parts.hour} maxLength={2} placeholder="HH" width={52} onChange={(v) => update('hour', v)} />
        <span className="text-[16px]" style={{ color: 'var(--sc-text-mute)' }} aria-hidden="true">
          :
        </span>
        <NumberBox id={`${id}-minute`} label="Minute" value={parts.minute} maxLength={2} placeholder="MM" width={52} onChange={(v) => update('minute', v)} />
        <fieldset className="flex rounded-xl overflow-hidden ml-1" style={{ border: '1px solid rgba(255,255,255,0.12)' }} aria-label="AM or PM">
          {['AM', 'PM'].map((half) => (
            <button
              key={half}
              type="button"
              onClick={() => update('ampm', half)}
              aria-pressed={parts.ampm === half}
              className="px-3 py-2.5 text-[13px] font-bold"
              style={parts.ampm === half ? { background: '#FFC20E', color: '#000' } : { background: 'rgba(0,0,0,0.34)', color: 'var(--sc-text-mute)' }}
              data-testid={`${id}-${half.toLowerCase()}`}
            >
              {half}
            </button>
          ))}
        </fieldset>
      </div>
    </div>
  );
};

export default LaunchAtFields;
